<script>
import 'intersection-observer';

/**
 * This is an abstract component that is used to determine if the child component's bounding box it wraps intersects with a given bounding box.
 * It can only render 1 child component.
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */
export default {
  name: 'IntersectionObserver',
  abstract: true,
  props: {
    /**
     * Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed.
     * If you only want to detect when visibility passes the 50% mark, you can use a value of 0.5.
     * If you want the callback to run every time visibility passes another 25%, you would specify the array [0, 0.25, 0.5, 0.75, 1].
     * The default is 0 (meaning as soon as even one pixel is visible, the callback will be run).
     * A value of 1.0 means that the threshold isn't considered passed until every pixel is visible.
     */
    threshold: {
      type: [Number, Array],
      default: 0,
    },

    /**
     * The element that is used as the viewport for checking visiblity of the target.
     * Must be the ancestor of the target.
     * Defaults to the browser viewport if not specified or if null.
     */
    root: {
      required: false,
      default: null,
    },

    /**
     * Margin around the root.
     * Can have values similar to the CSS margin property, e.g. "10px 20px 30px 40px" (top, right, bottom, left).
     * The values can be percentages. This set of values serves to grow or shrink each side of the root element's bounding box before computing intersections.
     * Defaults to all zeros.
     */
    rootMargin: {
      type: String,
      default: '0px 0px 0px 0px',
    },
  },
  emits: [
    /**
     * Emitted when the observer enters the viewport
     */
    'intersect-enter',
    /**
     * Emitted when the observer leaves the viewport
     */
    'intersect-leave',
  ],
  mounted() {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver((entries) => {
      this.$emit(entries[0].isIntersecting ? 'intersect-enter' : 'intersect-leave', [entries[0]]);
    }, {
      root: this.root,
      rootMargin: this.rootMargin,
      threshold: this.threshold,
    });

    this.$nextTick(() => {
      this.observer.observe(this.$el);
    });
  },
  unmounted() {
    this.observer.disconnect();
  },
  render() {
    try {
      return this.$slots.default()[0];
    } catch (e) {
      throw new Error('IntersectionObserver.vue can only render one, and exactly one child component.');
    }
  },
};
</script>
